import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import DashboardHeader from "../../../../components/dashboard/dashboardHeader";
import { ResourceLoading } from "../../../../components/utils/ResourceLoading";
import { createMeetingMinutesTemplate, fetchMeetingMinutesTemplate, updateMeetingMinutesTemplate } from "../../../../api/meetingMinutes";
import toast from "react-hot-toast";


export const MeetingMinutesTemplateEditor = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    meeting_type: "",
    active: true,
  });

  useEffect(() => {
    if (isEdit) {
      const fetchData = async () => {
        try {
          setLoading(true);
          const data = await fetchMeetingMinutesTemplate(id);
          setFormData({
            title: data.title || "",
            description: data.description || "",
            meeting_type: data.meeting_type || "",
            active: data.active !== false,
          });
        } catch (err) {
          console.error("Error fetching template:", err);
          toast.error(t("meeting_minutes.template.load_error"));
        } finally {
          setLoading(false);
        }
      };
      fetchData();
    }
  }, [id, isEdit]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      if (isEdit) {
        await updateMeetingMinutesTemplate({ ...formData, id });
        toast.success(t("meeting_minutes.template.updated"));
      } else {
        await createMeetingMinutesTemplate(formData);
        toast.success(t("meeting_minutes.template.created"));
      }
      navigate("/dashboard/meeting-minutes/templates");
    } catch (err) {
      console.error("Error saving template:", err);
      toast.error(isEdit ? t("meeting_minutes.template.update_error") : t("meeting_minutes.template.create_error"));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <ResourceLoading />;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <DashboardHeader
        title={isEdit ? t("meeting_minutes.templates.edit") : t("meeting_minutes.templates.new")}
      />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <form onSubmit={handleSubmit} className="bg-white shadow rounded-lg p-6">
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                {t("meeting_minutes.title")} *
              </label>
              <input
                type="text"
                required
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                {t("meeting_minutes.meeting_type")}
              </label>
              <select
                value={formData.meeting_type}
                onChange={(e) => setFormData({ ...formData, meeting_type: e.target.value })}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
              >
                <option value="">{t("meeting_minutes.select_type")}</option>
                <option value="employee_interview">{t("meeting_minutes.type.employee_interview")}</option>
                <option value="project_meeting">{t("meeting_minutes.type.project_meeting")}</option>
                <option value="other">{t("meeting_minutes.type.other")}</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                {t("meeting_minutes.description")}
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={4}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                checked={formData.active}
                onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
              />
              <label className="ml-2 block text-sm text-gray-900">
                {t("active")}
              </label>
            </div>

            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => navigate("/dashboard/meeting-minutes/templates")}
                className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                {t("Cancel")}
              </button>
              <button
                type="submit"
                disabled={saving}
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50"
              >
                {saving ? t("Saving...") : (isEdit ? t("Update") : t("Create"))}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

